import { httpsCallable, type Functions } from 'firebase/functions'
import { api } from 'config/api'
import type { RubricConfigPayload } from 'pages/instructor/assignment/dialogs/customRubricsConfig'

type SaveCustomRubricsArgs = {
  /** When true (the `useApiForCustomRubrics` flag), save via the AdonisJS
   * server endpoint; otherwise via the cloud function. */
  useApi: boolean
  functions: Functions
  sectionId: string
  assignmentId: string
  rubricConfigs: RubricConfigPayload[]
}

/**
 * Persists per-assignment custom rubric config, routed by the
 * `useApiForCustomRubrics` feature flag. Throws on failure — callers surface a
 * generic save-failed toast and roll back.
 */
export async function saveCustomRubrics({
  useApi,
  functions,
  sectionId,
  assignmentId,
  rubricConfigs,
}: SaveCustomRubricsArgs): Promise<void> {
  if (useApi) {
    const result = await api.v1.assignments.rubrics.upsert({
      body: { sectionId, assignmentId, rubricConfigs },
    })

    if (!result.success) {
      throw new Error('failed to save custom rubrics')
    }

    return
  }

  const createOrUpdateAssignmentRubrics = httpsCallable<
    {
      sectionId: string
      assignmentId: string
      rubricConfigs: RubricConfigPayload[]
    },
    { success: boolean; error?: string }
  >(functions, 'createOrUpdateAssignmentRubrics')
  const { data: result } = await createOrUpdateAssignmentRubrics({
    sectionId,
    assignmentId,
    rubricConfigs,
  })
  if (!result.success) {
    throw new Error(result.error ?? 'failed to save custom rubrics')
  }
}
